import { Canvas } from "@react-three/fiber";
import { OrbitControls, PerspectiveCamera } from "@react-three/drei";
import { useCallback, useEffect, useRef } from "react";
import * as THREE from "three";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { useSceneStore } from "../store/sceneStore";
import { SceneContent } from "./SceneContent";
import type { ViewPreset } from "../types";

const VIEW_DIRECTIONS: Record<ViewPreset, THREE.Vector3> = {
  front: new THREE.Vector3(0, 0.15, 1),
  top: new THREE.Vector3(0, 1, 0.0001),
  iso: new THREE.Vector3(1, 0.85, 1),
};

export function Viewport() {
  const cameraRef = useRef<THREE.PerspectiveCamera>(null);
  const orbitControlsRef = useRef<OrbitControlsImpl>(null);
  const objectsGroupRef = useRef<THREE.Group>(null!);
  const selectObject = useSceneStore((s) => s.selectObject);
  const requestedView = useSceneStore((s) => s.requestedView);
  const clearRequestedView = useSceneStore((s) => s.clearRequestedView);

  const setView = useCallback((preset: ViewPreset) => {
    const camera = cameraRef.current;
    const controls = orbitControlsRef.current;
    const group = objectsGroupRef.current;
    if (!camera || !controls) return;

    const box = new THREE.Box3();
    if (group) box.setFromObject(group);
    const hasContent = !box.isEmpty();
    const center = hasContent ? box.getCenter(new THREE.Vector3()) : new THREE.Vector3();
    const size = hasContent ? box.getSize(new THREE.Vector3()) : new THREE.Vector3(100, 100, 100);
    const radius = Math.max(size.length() * 0.6, 50);

    const direction = VIEW_DIRECTIONS[preset];
    camera.position.copy(center).addScaledVector(direction, radius);
    controls.target.copy(center);
    camera.near = Math.max(radius / 1000, 0.1);
    camera.far = radius * 50;
    camera.updateProjectionMatrix();
    controls.update();
  }, []);

  useEffect(() => {
    if (!requestedView) return;
    setView(requestedView);
    clearRequestedView();
  }, [requestedView, setView, clearRequestedView]);

  return (
    <div className="stage">
      <Canvas shadows onPointerMissed={() => selectObject(null)}>
        <PerspectiveCamera ref={cameraRef} makeDefault position={[150, 130, 150]} fov={45} near={0.1} far={5000} />
        <OrbitControls ref={orbitControlsRef} makeDefault enableDamping dampingFactor={0.15} />
        <SceneContent objectsGroupRef={objectsGroupRef} orbitControlsRef={orbitControlsRef} />
      </Canvas>
    </div>
  );
}
